import {
  IAyaEnvelope,
  IAyaEnvelopeRaw,
  isAyaEnvelopeRaw,
  isTerminalEvent,
  toAyaEnvelope,
  toCursor
} from '../interfaces/aya-stream.interface';
import { EnumFrameOutcome } from '../enums/frame-outcome.enum';
import { ISSEFrame } from './sse-parser';

export interface IFrameResult {
  outcome: EnumFrameOutcome;
  envelope: IAyaEnvelope | null;
  terminal: boolean;
}

const ignored = (outcome: EnumFrameOutcome): IFrameResult => ({
  outcome,
  envelope: null,
  terminal: false
});

/**
 * Guarda o cursor fora da conexão para que um join retome de onde parou. `seq`
 * cresce, mas não é contíguo: filtros, aliases e replay deixam buracos válidos,
 * então ele só serve para ordenar e descartar repetido.
 */
export class AyaStreamReducer {
  private lastSeq = 0;
  private terminated = false;

  constructor(
    private readonly sessionId: string,
    private readonly runId: string
  ) {}

  get cursor(): string | null {
    return this.lastSeq ? toCursor(this.runId, this.lastSeq) : null;
  }

  get isTerminated(): boolean {
    return this.terminated;
  }

  apply(frame: ISSEFrame): IFrameResult {
    if (this.terminated) {
      return ignored(EnumFrameOutcome.AFTER_TERMINAL);
    }

    const raw = this.parse(frame.data);
    if (!raw) {
      return ignored(EnumFrameOutcome.INVALID);
    }

    if (raw.session_id !== this.sessionId || raw.run_id !== this.runId) {
      return ignored(EnumFrameOutcome.FOREIGN);
    }

    if (frame.id !== null && frame.id !== toCursor(raw.run_id, raw.seq)) {
      return ignored(EnumFrameOutcome.INVALID);
    }

    if (raw.seq <= this.lastSeq) {
      return ignored(EnumFrameOutcome.DUPLICATE);
    }

    this.lastSeq = raw.seq;
    this.terminated = isTerminalEvent(raw.type);

    return {
      outcome: EnumFrameOutcome.APPLIED,
      envelope: toAyaEnvelope(raw),
      terminal: this.terminated
    };
  }

  private parse(data: string): IAyaEnvelopeRaw | null {
    try {
      const parsed: unknown = JSON.parse(data);
      return isAyaEnvelopeRaw(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}
